import { useAppCopy } from '@/i18n/context';
import { useNamespaceCopy } from '@/i18n/ns';
import artifactPanelsCopy from '@/i18n/ns/artifact-panels';
import knowledgePagesCopy from '@/i18n/ns/knowledge-pages';
import storefrontCopy from '@/i18n/ns/storefront';
import meetingsRecordCopy from '@/i18n/ns/meetings-record';
import searchCopy from '@/i18n/ns/search';
import workspaceTopbarCopy from '@/i18n/ns/workspace-topbar';
import { isSettingsView, type viewDefinitions, type WorkspaceView } from './view-definitions';

type SettingsView = {
  [View in WorkspaceView]: (typeof viewDefinitions)[View]['group'] extends 'personal' | 'company'
    ? View
    : never;
}[WorkspaceView];

/**
 * The top bar's words in the reader's language. Each place is named as it already names itself: the Library, Apps,
 * Meetings and Company knowledge titles, Search's New chat and Skills, and the app's Settings for every settings
 * view. The rest is `workspace-topbar`. A new view must be named here before it type-checks.
 */
export function useTopbarCopy() {
  const topbar = useNamespaceCopy(workspaceTopbarCopy);
  const search = useNamespaceCopy(searchCopy);
  const panels = useNamespaceCopy(artifactPanelsCopy);
  const meetings = useNamespaceCopy(meetingsRecordCopy);
  const knowledge = useNamespaceCopy(knowledgePagesCopy);
  const storefront = useNamespaceCopy(storefrontCopy);
  const app = useAppCopy();
  const places: Record<Exclude<WorkspaceView, SettingsView>, string> = {
    chat: search.newChat,
    inbox: topbar.inbox,
    files: panels.libraryTitle,
    results: panels.libraryTitle,
    apps: panels.libraryKindApps,
    storefront: storefront.pageTitle,
    meetings: meetings.pageTitle,
    skills: search.groupSkills,
    projects: topbar.projects,
    agents: topbar.agents,
    knowledge: knowledge.pageTitle,
    connections: topbar.connections,
  };
  return {
    ...topbar,
    newChat: search.newChat,
    place: (view: WorkspaceView) =>
      isSettingsView(view) ? app.settings : places[view as Exclude<WorkspaceView, SettingsView>],
  };
}
